import React from 'react';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import { DisputeReviewFragmentType } from '@pay-bill/graphql';
import { staffingRequestTypeHumanized } from 'works/legacy/services/staffing-request-type-service';
import { DisputeLastComment, DisputeState } from '@pay-bill/components';
import { Link, Col, Row, Text, Divider } from '@common/components';
import { useCurrentEntity } from '@identity/hooks';
import { getRedirectTargetEntity } from '@pay-bill/services';

export const DisputeBodyDetails = ({
  dispute,
  showStatus = true,
  showReason = true,
  showLastComment = true,
}: {
  dispute: DisputeReviewFragmentType;
  showStatus?: boolean;
  showReason?: boolean;
  showLastComment?: boolean;
}) => {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;

  const fullName = dispute.staffingRequestAssignment.worksProfile.fullName;
  const openedBy =
    dispute.createdBy?.entityType === 'Supplier'
      ? dispute.supplier.name
      : dispute.organization.name;

  return (
    <div className="dispute-body-details">
      {showStatus && (
        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Status</Text>
          </Col>
          <Col span={16} id="dispute-state">
            <DisputeState
              createdBy={getRedirectTargetEntity(dispute.createdBy?.entityType)}
              state={dispute.state}
            />
          </Col>
        </Row>
      )}

      {dispute.rejectionReason && (
        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Rejection Reason</Text>
          </Col>
          <Col span={16} id="dispute-rejection-reason">
            <span>{dispute.rejectionReason.name}</span>
          </Col>
        </Row>
      )}

      <Row className="dispute-details">
        <Col span={8}>
          <Text secondary>Dispute #</Text>
        </Col>
        <Col span={16} id="dispute-code">
          <span>{dispute.code}</span>
        </Col>
      </Row>

      {showReason && (
        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Open Reason</Text>
          </Col>
          <Col span={16} id="dispute-creation-reason">
            <span>{dispute.creationReason}</span>
          </Col>
        </Row>
      )}

      <Row className="dispute-details">
        <Col span={8}>
          <Text secondary>Opened by</Text>
        </Col>
        <Col span={16} id="dispute-opened-by">
          <span>{openedBy}</span>
        </Col>
      </Row>

      <Divider gutter={16} />

      <Row className="dispute-details">
        <Col span={8}>
          <Text secondary className="sentry-mask">
            Name
          </Text>
        </Col>
        <Col span={16} id="dispute-nurse-name">
          {entityType === 'Organization' ? (
            <Link
              className="color-elm"
              to={`/works/team/${dispute.staffingRequestType}/${dispute.staffingRequestAssignment.id}`}
              target="_blank"
            >
              <strong>{fullName}</strong>
            </Link>
          ) : (
            <Link
              className="color-elm"
              to={`/works/supplier/candidates/${dispute.staffingRequestAssignment.worksProfile.id}`}
              target="_blank"
            >
              <strong>{fullName}</strong>
            </Link>
          )}
        </Col>
      </Row>

      <Row className="dispute-details">
        <Col span={8}>
          <Text secondary>
            {entityType === 'Organization' ? 'Invoice #' : 'Reverse Invoice #'}
          </Text>
        </Col>
        <Col span={16} id="rinv-code">
          {entityType === 'Organization' ? (
            <Link
              className="color-elm"
              to={`/works/billing/invoices/${dispute.worksInvoice.id}/hours`}
              target="_blank"
            >
              <strong>{dispute.worksInvoice.code}</strong>
            </Link>
          ) : (
            <Link
              className="color-elm"
              to={`/works/supplier/billing/reverse-invoices/${dispute.worksReverseInvoice.id}`}
              target="_blank"
            >
              <strong>{dispute.worksReverseInvoice.code}</strong>
            </Link>
          )}
        </Col>
      </Row>

      <Row className="dispute-details">
        <Col span={8}>
          <Text secondary>
            {entityType === 'Organization' ? 'Supplier' : 'Organization'}
          </Text>
        </Col>
        {entityType === 'Organization' ? (
          <Col span={16} id="dispute-supplier">
            <span>{dispute.supplier.name}</span>
          </Col>
        ) : (
          <Col span={16} id="dispute-organization">
            <span>{dispute.organization.name}</span>
          </Col>
        )}
      </Row>

      <Row className="dispute-details">
        <Col span={8}>
          <Text secondary>Date Worked</Text>
        </Col>
        <Col span={16} id="dispute-date">
          <span>{toUSCompactDateFormat(dispute.newDate)}</span>
        </Col>
      </Row>

      <Row className="dispute-details">
        <Col span={8}>
          <Text secondary>Contract Type</Text>
        </Col>
        <Col span={16} id="dispute-contract-type">
          <span>
            {staffingRequestTypeHumanized(dispute.staffingRequestType)}
          </span>
        </Col>
      </Row>

      <Divider gutter={16} />

      {showLastComment && dispute.lastComment && (
        <>
          <DisputeLastComment comment={dispute.lastComment} />
          <Divider gutter={16} />
        </>
      )}
      <style jsx>{`
        .dispute-body-details :global(.dispute-details) {
          margin-bottom: 8px;
        }
      `}</style>
    </div>
  );
};
